import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../Config/Config";
import Header from "../layout/Header";

// ─── Design tokens ────────────────────────────────────────────────
const COLOR = {
  navy: "#0B1D3A",
  navyMid: "#162d57",
  gold: "#C9A84C",
  goldLight: "#f0e0a8",
  bg: "#F7F5F0",
  cardBg: "#FFFFFF",
  slate: "#4A5568",
  muted: "#8896A5",
  border: "#DDE3EE",
  staffTag: "#1a56db",
  stuTag: "#0e7f57",
};

const FONT = {
  display: "'Playfair Display', Georgia, serif",
  body: "'Inter', system-ui, sans-serif",
};

// ─── Google Font loader ───────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@400;500;600&display=swap";
document.head.appendChild(fontLink);

// ─── Inline global styles ─────────────────────────────────────────
const globalStyles = `
  * { box-sizing: border-box; }
  body { background: ${COLOR.bg}; font-family: ${FONT.body}; }

  .dir-card {
    background: ${COLOR.cardBg};
    border-radius: 14px;
    overflow: hidden;
    border-left: 4px solid transparent;
    box-shadow: 0 2px 12px rgba(11,29,58,.07);
    transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease;
    height: 100%;
  }
  .dir-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 32px rgba(11,29,58,.13);
    border-left-color: ${COLOR.gold};
  }

  .dir-avatar {
    width: 64px; height: 64px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 26px; font-weight: 700;
    font-family: ${FONT.display};
    color: #fff;
    flex-shrink: 0;
    border: 3px solid rgba(255,255,255,.25);
  }

  .dir-tab-btn {
    padding: 10px 28px;
    border: 2px solid ${COLOR.border};
    border-radius: 50px;
    background: transparent;
    font-family: ${FONT.body};
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all .18s ease;
    color: ${COLOR.slate};
    position: relative;
  }
  .dir-tab-btn.active-staff {
    background: ${COLOR.navy};
    border-color: ${COLOR.navy};
    color: #fff;
  }
  .dir-tab-btn.active-student {
    background: ${COLOR.stuTag};
    border-color: ${COLOR.stuTag};
    color: #fff;
  }
  .dir-tab-btn:not(.active-staff):not(.active-student):hover {
    border-color: ${COLOR.gold};
    color: ${COLOR.navy};
  }

  .dir-search {
    border: 2px solid ${COLOR.border};
    border-radius: 50px;
    padding: 13px 22px 13px 48px;
    font-size: 15px;
    font-family: ${FONT.body};
    width: 100%;
    background: #fff;
    outline: none;
    color: ${COLOR.navy};
    transition: border-color .18s;
  }
  .dir-search:focus { border-color: ${COLOR.gold}; }

  .dir-dept-heading {
    font-family: ${FONT.display};
    font-size: 19px;
    font-weight: 700;
    color: ${COLOR.navy};
    letter-spacing: -.2px;
    padding-bottom: 10px;
    border-bottom: 2px solid ${COLOR.goldLight};
    margin-bottom: 20px;
    display: flex; align-items: center; gap: 10px;
  }
  .dir-dept-heading span.dept-count {
    font-family: ${FONT.body};
    font-size: 13px;
    font-weight: 600;
    background: ${COLOR.goldLight};
    color: ${COLOR.navy};
    border-radius: 20px;
    padding: 2px 10px;
  }

  .dir-stat-pill {
    background: #fff;
    border: 1.5px solid ${COLOR.border};
    border-radius: 40px;
    padding: 8px 20px;
    font-size: 14px;
    font-weight: 500;
    color: ${COLOR.slate};
    display: inline-flex; align-items: center; gap: 6px;
  }
  .dir-stat-num {
    font-weight: 700;
    font-size: 17px;
  }

  .dir-banner {
    background: linear-gradient(135deg, ${COLOR.navy} 0%, ${COLOR.navyMid} 100%);
    padding: 64px 24px 48px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .dir-banner::before {
    content: '';
    position: absolute; inset: 0;
    background: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 28px,
      rgba(201,168,76,.05) 28px,
      rgba(201,168,76,.05) 29px
    );
    pointer-events: none;
  }

  .dir-contact-btn {
    border: 1.5px solid ${COLOR.border};
    border-radius: 6px;
    padding: 5px 12px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    background: transparent;
    text-decoration: none;
    transition: background .15s, color .15s;
    display: inline-flex; align-items: center; gap: 5px;
  }
  .dir-contact-btn.staff { color: ${COLOR.staffTag}; border-color: #c3d6fc; }
  .dir-contact-btn.staff:hover { background: #eff4ff; }
  .dir-contact-btn.student { color: ${COLOR.stuTag}; border-color: #b8f0da; }
  .dir-contact-btn.student:hover { background: #f0fdf8; }

  .dir-empty {
    text-align: center;
    padding: 60px 20px;
    color: ${COLOR.muted};
    font-size: 15px;
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────
function groupByDepartment(data, search) {
  const q = search.toLowerCase();
  const filtered = q
    ? data.filter((item) => JSON.stringify(item).toLowerCase().includes(q))
    : data;

  const grouped = filtered.reduce((acc, item) => {
    const dept = item.Department || "Other";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(item);
    return acc;
  }, {});

  return Object.keys(grouped)
    .sort()
    .reduce((acc, key) => {
      acc[key] = grouped[key];
      return acc;
    }, {});
}

// ─── Card ─────────────────────────────────────────────────────────
function PersonCard({ item, variant }) {
  const isStaff = variant === "staff";
  const avatarBg = isStaff
    ? `linear-gradient(135deg, ${COLOR.navy}, ${COLOR.navyMid})`
    : `linear-gradient(135deg, #0e7f57, #0a5e40)`;

  return (
    <div className="dir-card">
      <div style={{ padding: "18px 18px 14px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
          <div className="dir-avatar" style={{ background: avatarBg }}>
            {item.Name?.charAt(0)?.toUpperCase() ?? "?"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontFamily: FONT.display,
                fontWeight: 700,
                fontSize: "16px",
                color: COLOR.navy,
                margin: "0 0 2px",
                lineHeight: 1.3,
              }}
            >
              {item.Name || "—"}
            </p>
            <p
              style={{
                fontSize: "13px",
                color: COLOR.muted,
                margin: 0,
                fontWeight: 500,
              }}
            >
              {item.Designation || "—"}
            </p>
          </div>
        </div>

        {item.Department && (
          <p
            style={{
              marginTop: "12px",
              fontSize: "12px",
              fontWeight: 600,
              color: COLOR.gold,
              textTransform: "uppercase",
              letterSpacing: ".6px",
              marginBottom: 0,
            }}
          >
            {item.Department}
          </p>
        )}
        {item.Email && (
          <p
            style={{
              marginTop: "12px",
              fontSize: "12px",
              fontWeight: 600,
              color: COLOR.gold,
              textTransform: "uppercase",
              letterSpacing: ".6px",
              marginBottom: 0,
            }}
          >
            {item.Email}
          </p>
        )}
      </div>

      <div
        style={{
          borderTop: `1px solid ${COLOR.border}`,
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {item.MobileNumber ? (
          <a
            href={`tel:${item.MobileNumber}`}
            className={`dir-contact-btn ${isStaff ? "staff" : "student"}`}
          >
            📞 {item.MobileNumber}
          </a>
        ) : (
          <span style={{ fontSize: "13px", color: COLOR.muted }}>
            No contact
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────
export default function Index() {
  const [allData, setAllData] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("staff");

  const staffData = allData.filter((i) => i.Category === "Staff");
  const studentData = allData.filter((i) => i.Category === "PG Student");

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "NDC", "ContactDetails"), (snap) => {
      if (snap.exists()) {
        const tmp = Object.entries(snap.data()).map(([key, val]) => ({
          Id: key,
          ...val,
        }));
        tmp.sort((a, b) => a.Name.localeCompare(b.Name));
        setAllData(tmp);
      } else {
        setAllData([]);
      }
    });
    return () => unsub();
  }, []);

  const groupedStaff = groupByDepartment(staffData, search);
  const groupedStudents = groupByDepartment(studentData, search);
  const totalStaff = Object.values(groupedStaff).flat().length;
  const totalStudents = Object.values(groupedStudents).flat().length;

  const activeGroup = activeTab === "staff" ? groupedStaff : groupedStudents;
  const activeTotal = activeTab === "staff" ? totalStaff : totalStudents;

  return (
    <div
      style={{
        fontFamily: FONT.body,
        background: COLOR.bg,
        minHeight: "100vh",
      }}
    >
      <style>{globalStyles}</style>
      <Header />

      {/* ── Banner ─────────────────────────────────────────────── */}
      <div className="dir-banner">
        <p
          style={{
            fontFamily: FONT.body,
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: COLOR.gold,
            marginBottom: "10px",
            marginTop: "4rem",
          }}
        >
          NDC · Academic Directory
        </p>
        <h1
          style={{
            fontFamily: FONT.display,
            fontSize: "clamp(28px,5vw,48px)",
            fontWeight: 700,
            color: "#fff",
            margin: "0 0 12px",
            lineHeight: 1.2,
          }}
        >
          Faculty &amp; PG Students
        </h1>
        <p
          style={{
            color: "rgba(255,255,255,.65)",
            fontSize: "16px",
            margin: 0,
          }}
        >
          Contact information for faculty members and postgraduate students
        </p>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            flexWrap: "wrap",
            marginTop: "28px",
          }}
        >
          <div className="dir-stat-pill">
            <span className="dir-stat-num" style={{ color: COLOR.staffTag }}>
              {staffData.length}
            </span>
            Faculty
          </div>
          <div className="dir-stat-pill">
            <span className="dir-stat-num" style={{ color: COLOR.stuTag }}>
              {studentData.length}
            </span>
            PG Students
          </div>
        </div>
      </div>

      {/* ── Sticky control bar ─────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(247,245,240,.96)",
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${COLOR.border}`,
          padding: "14px 24px",
        }}
      >
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search */}
          <div style={{ flex: 1, minWidth: "220px", position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: "17px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                pointerEvents: "none",
              }}
            >
              🔍
            </span>
            <input
              className="dir-search"
              type="text"
              placeholder="Search by name, department, designation…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className={`dir-tab-btn ${activeTab === "staff" ? "active-staff" : ""}`}
              onClick={() => setActiveTab("staff")}
            >
              Faculty
              <span
                style={{
                  marginLeft: "6px",
                  background:
                    activeTab === "staff"
                      ? "rgba(255,255,255,.2)"
                      : COLOR.border,
                  color: activeTab === "staff" ? "#fff" : COLOR.slate,
                  borderRadius: "20px",
                  padding: "1px 8px",
                  fontSize: "12px",
                }}
              >
                {totalStaff}
              </span>
            </button>
            <button
              className={`dir-tab-btn ${activeTab === "student" ? "active-student" : ""}`}
              onClick={() => setActiveTab("student")}
            >
              PG Students
              <span
                style={{
                  marginLeft: "6px",
                  background:
                    activeTab === "student"
                      ? "rgba(255,255,255,.2)"
                      : COLOR.border,
                  color: activeTab === "student" ? "#fff" : COLOR.slate,
                  borderRadius: "20px",
                  padding: "1px 8px",
                  fontSize: "12px",
                }}
              >
                {totalStudents}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Main content ───────────────────────────────────────── */}
      <div
        style={{
          maxWidth: "1240px",
          margin: "0 auto",
          padding: "40px 24px 80px",
        }}
      >
        {Object.keys(activeGroup).length === 0 ? (
          <div className="dir-empty">
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔍</div>
            No results found. Try a different search term.
          </div>
        ) : (
          Object.entries(activeGroup).map(([dept, members]) => (
            <div key={dept} style={{ marginBottom: "44px" }}>
              <div className="dir-dept-heading">
                {dept}
                <span className="dept-count">{members.length}</span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "16px",
                }}
              >
                {members.map((item) => (
                  <PersonCard key={item.Id} item={item} variant={activeTab} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
